import React, { useState, useRef, useEffect, useCallback } from 'react';
import { X, Send } from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const LOAN_OPTIONS = ['Home Loan', 'Business Loan', 'Loan Against Property', 'Personal Loan', 'Working Capital', 'Other'];
const AMOUNT_OPTIONS = ['Under ₹25 Lakh', '₹25 Lakh – ₹1 Cr', '₹1 Cr – ₹5 Cr', 'Above ₹5 Cr'];

export const ChatBot = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [step, setStep] = useState('requirement');
  const [input, setInput] = useState('');
  const [lead, setLead] = useState({ requirement: '', loan_amount: '', name: '', mobile: '' });
  const [showHelpTip, setShowHelpTip] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    const timer = setTimeout(() => setShowHelpTip(true), 2500);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, isOpen]);

  useEffect(() => {
    if (isOpen && messages.length === 0) {
      setMessages([{ from: 'bot', text: "Hi! I'm Yardy, your LOANYARD CAPITAL assistant. I'll help you find the right loan in under a minute. What type of loan are you looking for?" }]);
    }
  }, [isOpen, messages.length]);

  const addBot = (text) => setMessages((prev) => [...prev, { from: 'bot', text }]);
  const addUser = (text) => setMessages((prev) => [...prev, { from: 'user', text }]);

  const submitLead = useCallback(async (finalLead) => {
    try {
      await axios.post(`${API}/api/chat-lead`, finalLead);
      addBot(`Thank you, ${finalLead.name}! Your details are with our loan experts — expect a call on ${finalLead.mobile} shortly. For an instant response, tap the button below to continue on WhatsApp.`);
    } catch (err) {
      addBot('Something went wrong saving your details. Please call us directly at +91 98927 59155.');
    }
    setStep('done');
  }, []);

  const handleAnswer = (answer) => {
    if (!answer.trim()) return;
    addUser(answer);

    if (step === 'requirement') {
      setLead((prev) => ({ ...prev, requirement: answer }));
      addBot('Great choice! Approximately how much loan amount do you need?');
      setStep('amount');
    } else if (step === 'amount') {
      setLead((prev) => ({ ...prev, loan_amount: answer }));
      addBot('Perfect. May I know your name?');
      setStep('name');
    } else if (step === 'name') {
      setLead((prev) => ({ ...prev, name: answer }));
      addBot(`Nice to meet you, ${answer}! Please share your 10-digit mobile number so our loan expert can call you with the best offers.`);
      setStep('mobile');
    } else if (step === 'mobile') {
      const digits = answer.replace(/\D/g, '');
      if (digits.length < 10 || digits.length > 12) {
        addBot('That does not look like a valid mobile number. Please enter your 10-digit mobile number.');
        return;
      }
      const finalLead = { ...lead, mobile: digits.slice(-10) };
      setLead(finalLead);
      submitLead(finalLead);
    }
    setInput('');
  };

  const quickReplies = step === 'requirement' ? LOAN_OPTIONS : step === 'amount' ? AMOUNT_OPTIONS : [];

  return (
    <>
      {/* Launcher Button */}
      {!isOpen && (
        <div className="fixed bottom-28 right-6 z-50 flex flex-col items-end">
          {/* Tooltip above Ask Yardy */}
          {showHelpTip && (
            <div className="relative mb-3 mr-1 bg-white px-4 py-2.5 rounded-lg shadow-xl border border-slate-200 animate-in slide-in-from-bottom duration-300" data-testid="yardy-help-tooltip">
              <button
                onClick={() => setShowHelpTip(false)}
                data-testid="yardy-tooltip-close"
                aria-label="Dismiss"
                className="absolute -top-2 -right-2 w-5 h-5 bg-slate-800 text-white rounded-full flex items-center justify-center hover:bg-slate-900"
              >
                <X className="w-3 h-3" />
              </button>
              <p className="text-sm text-slate-700 font-medium whitespace-nowrap">Need help? Chat with us! 💬</p>
              <span className="absolute -bottom-1.5 right-7 w-3 h-3 bg-white border-b border-r border-slate-200 rotate-45"></span>
            </div>
          )}
          <button
            onClick={() => setIsOpen(true)}
            data-testid="chatbot-launcher-btn"
            aria-label="Chat with Yardy"
            className="flex items-center gap-2.5 bg-emerald-600 hover:bg-emerald-700 text-white pl-1.5 pr-5 py-1.5 rounded-full shadow-lg transition-all duration-300 hover:scale-105"
          >
            <span className="w-11 h-11 rounded-full bg-white overflow-hidden flex items-start justify-center ring-2 ring-emerald-300/60 yardy-doll-motion">
              <img src="/img/yardy-doll.png" loading="lazy" alt="Yardy doll" data-testid="yardy-doll" className="w-full h-full object-cover object-top scale-110" />
            </span>
            <span className="font-semibold text-sm">Ask Yardy</span>
          </button>
        </div>
      )}

      {/* Chat Window */}
      {isOpen && (
        <div
          data-testid="chatbot-window"
          className="fixed bottom-6 right-6 z-[60] w-[92vw] max-w-sm bg-white rounded-2xl shadow-2xl border border-slate-200 flex flex-col overflow-hidden"
          style={{ height: '480px' }}
        >
          {/* Header */}
          <div className="bg-emerald-600 text-white px-4 py-3 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="w-9 h-9 bg-white rounded-full overflow-hidden flex items-start justify-center yardy-doll-motion">
                <img src="/img/yardy-doll.png" loading="lazy" alt="Yardy doll" className="w-full h-full object-cover object-top scale-110" />
              </div>
              <div>
                <p className="font-bold text-sm leading-tight">Yardy — Loan Assistant</p>
                <p className="text-xs text-emerald-100">LOANYARD CAPITAL • Online</p>
              </div>
            </div>
            <button onClick={() => setIsOpen(false)} data-testid="chatbot-close-btn" aria-label="Close chat" className="hover:bg-white/20 rounded-full p-1 transition-colors">
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Messages */}
          <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3 bg-slate-50">
            {messages.map((msg, idx) => (
              <div key={`msg-${idx}`} className={`flex ${msg.from === 'user' ? 'justify-end' : 'justify-start'}`}>
                <div className={`max-w-[85%] px-4 py-2.5 rounded-2xl text-sm leading-relaxed ${
                  msg.from === 'user'
                    ? 'bg-emerald-600 text-white rounded-br-md'
                    : 'bg-white text-slate-700 border border-slate-200 rounded-bl-md shadow-sm'
                }`}>
                  {msg.text}
                </div>
              </div>
            ))}

            {/* Quick Replies */}
            {quickReplies.length > 0 && step !== 'done' && (
              <div className="flex flex-wrap gap-2 pt-1">
                {quickReplies.map((option) => (
                  <button
                    key={option}
                    onClick={() => handleAnswer(option)}
                    data-testid={`chatbot-option-${option.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
                    className="px-3 py-1.5 bg-white border border-emerald-300 text-emerald-700 rounded-full text-xs font-semibold hover:bg-emerald-600 hover:text-white hover:border-emerald-600 transition-colors"
                  >
                    {option}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Input */}
          {step !== 'done' && (
            <form
              onSubmit={(e) => { e.preventDefault(); handleAnswer(input); }}
              className="p-3 border-t border-slate-200 bg-white flex items-center gap-2"
            >
              <input
                type={step === 'mobile' ? 'tel' : 'text'}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder={step === 'mobile' ? 'Enter mobile number...' : 'Type your answer...'}
                data-testid="chatbot-input"
                className="flex-1 px-4 py-2.5 bg-slate-100 rounded-full text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
              />
              <button
                type="submit"
                data-testid="chatbot-send-btn"
                aria-label="Send message"
                className="w-10 h-10 bg-emerald-600 hover:bg-emerald-700 text-white rounded-full flex items-center justify-center transition-colors flex-shrink-0"
              >
                <Send className="w-4 h-4" />
              </button>
            </form>
          )}
          {step === 'done' && (
            <div className="p-3 border-t border-slate-200 bg-white space-y-2">
              <a
                href={`https://wa.me/919892759155?text=${encodeURIComponent(`Hi, I'm ${lead.name}. I'm interested in ${lead.requirement}${lead.loan_amount ? ` (${lead.loan_amount})` : ''}. Please call me on ${lead.mobile}.`)}`}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="chatbot-whatsapp-handoff"
                className="block w-full text-center bg-green-500 hover:bg-green-600 text-white font-semibold text-sm py-2.5 rounded-full transition-colors"
              >
                Continue on WhatsApp
              </a>
              <p className="text-xs text-slate-500 text-center">Our expert will contact you soon. Thank you!</p>
            </div>
          )}
        </div>
      )}
    </>
  );
};
